import { useQuery } from "@tanstack/react-query";

// Existing generic staff API helpers
import {
  fetchStaffDashboard,
  fetchStaffVerificationQueue,
  fetchStaffDisputeQueue,
  buildStaffVerificationQuery,
  buildStaffDisputeQuery,
  mapStaffProfileDto,
  mapStaffDashboardSummaryDto,
  mapStaffVerificationItemDto,
  mapStaffDisputeItemDto,
} from "./api";

import type {
  HnbStaffRole,
  StaffDashboardScope,
  StaffVerificationQueueStatus,
  StaffDisputeStatus,
  HnbStaffProfileDto,
  StaffDashboardSummaryDto,
  StaffProviderVerificationItemDto,
  StaffDisputeQueueItemDto,
  StaffDashboardResponseDto,
  StaffVerificationQueueResponseDto,
  StaffDisputeQueueResponseDto,
  HnbStaffProfile,
  StaffDashboardSummary,
  StaffProviderVerificationItem,
  StaffDisputeQueueItem,
  StaffVerificationFilters,
  StaffDisputeFilters,
} from "./api";

// Role-specific DTO imports
import type { MdDashboardResponseDto } from "./md.types";
import type {
  OperationsDashboardResponseDto,
  BdoWorkbenchResponseDto,
} from "./operations.types";
import type { CfoDashboardResponseDto } from "./cfo.types";
import type {
  FinanceDashboardResponseDto,
  FinanceOfficerWorkbenchResponseDto,
} from "./finance.types";
import type {
  AccountsDashboardResponseDto,
  AccountsOfficerWorkbenchResponseDto,
} from "./accounts.types";
import type {
  AuditDashboardResponseDto,
  AuditorWorkbenchResponseDto,
} from "./audit.types";
import type {
  HrDashboardResponseDto,
  HrManagerWorkbenchResponseDto,
} from "./hr.types";
import type {
  ItDashboardResponseDto,
  DeveloperWorkbenchResponseDto,
  DataScientistWorkbenchResponseDto,
} from "./it.types";
import type {
  GrievanceDashboardResponseDto,
  GrievanceOfficerWorkbenchResponseDto,
} from "./grievance.types";

const STAFF_API_BASE = "/api/staff";

type StaffQueryOptions = {
  enabled?: boolean;
};

type ApiEnvelope<T> = {
  success?: boolean;
  message?: string;
  data?: T;
};

async function fetchStaffJson<T>(path: string, errorLabel: string): Promise<T> {
  const response = await fetch(`${STAFF_API_BASE}${path}`, {
    method: "GET",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  });

  const payload = (await response.json().catch(() => null)) as
    | ApiEnvelope<T>
    | T
    | null;

  if (!response.ok) {
    const message =
      payload &&
      typeof payload === "object" &&
      "message" in payload &&
      typeof payload.message === "string"
        ? payload.message
        : `Failed to fetch ${errorLabel} (${response.status})`;

    throw new Error(message);
  }

  if (
    payload &&
    typeof payload === "object" &&
    "data" in payload &&
    (payload as ApiEnvelope<T>).data !== undefined
  ) {
    return (payload as ApiEnvelope<T>).data as T;
  }

  return payload as T;
}

/**
 * 1. Generic staff dashboard & queues
 *    (Existing behaviour retained)
 */

export function useStaffDashboard() {
  return useQuery<{
    profile: HnbStaffProfile;
    summary: StaffDashboardSummary;
  }>({
    queryKey: ["staffDashboard"],
    queryFn: () => fetchStaffDashboard(),
  });
}

export function useStaffVerificationQueue(
  filters: StaffVerificationFilters = {},
) {
  return useQuery<{
    items: StaffProviderVerificationItem[];
    total: number;
  }>({
    queryKey: ["staffVerificationQueue", filters],
    queryFn: () => fetchStaffVerificationQueue(filters),
  });
}

export function useStaffDisputeQueue(filters: StaffDisputeFilters = {}) {
  return useQuery<{
    items: StaffDisputeQueueItem[];
    total: number;
  }>({
    queryKey: ["staffDisputeQueue", filters],
    queryFn: () => fetchStaffDisputeQueue(filters),
  });
}

/**
 * 2. MD / CEO dashboard
 */

export function useMdDashboard(options: StaffQueryOptions = {}) {
  const { enabled = true } = options;

  return useQuery<MdDashboardResponseDto>({
    queryKey: ["mdDashboard"],
    queryFn: () => fetchStaffJson("/md/dashboard", "MD/CEO dashboard"),
    enabled,
  });
}

/**
 * 3. CFO dashboard
 *    CFO reports to MD/CEO; Finance & Accounts heads report to CFO.
 */

export function useCfoDashboard(options: StaffQueryOptions = {}) {
  const { enabled = true } = options;

  return useQuery<CfoDashboardResponseDto>({
    queryKey: ["cfoDashboard"],
    queryFn: () => fetchStaffJson("/cfo/dashboard", "CFO dashboard"),
    enabled,
  });
}

/**
 * 4. Operations Head dashboard + BDO workbench
 */

export function useOperationsDashboard(options: StaffQueryOptions = {}) {
  const { enabled = true } = options;

  return useQuery<OperationsDashboardResponseDto>({
    queryKey: ["operationsDashboard"],
    queryFn: () =>
      fetchStaffJson("/operations/dashboard", "Operations Head dashboard"),
    enabled,
  });
}

export function useBdoWorkbench(options: StaffQueryOptions = {}) {
  const { enabled = true } = options;

  return useQuery<BdoWorkbenchResponseDto>({
    queryKey: ["bdoWorkbench"],
    queryFn: () =>
      fetchStaffJson("/operations/workbench/bdo", "BDO workbench"),
    enabled,
  });
}

/**
 * 5. Finance Head dashboard + Finance Officer workbench
 */

export function useFinanceDashboard(options: StaffQueryOptions = {}) {
  const { enabled = true } = options;

  return useQuery<FinanceDashboardResponseDto>({
    queryKey: ["financeDashboard"],
    queryFn: () =>
      fetchStaffJson("/finance/dashboard", "Finance Head dashboard"),
    enabled,
  });
}

export function useFinanceOfficerWorkbench(options: StaffQueryOptions = {}) {
  const { enabled = true } = options;

  return useQuery<FinanceOfficerWorkbenchResponseDto>({
    queryKey: ["financeOfficerWorkbench"],
    queryFn: () =>
      fetchStaffJson("/finance/workbench/officer", "Finance Officer workbench"),
    enabled,
  });
}

/**
 * 6. Accounts Head dashboard + Accounts Officer workbench
 */

export function useAccountsDashboard(options: StaffQueryOptions = {}) {
  const { enabled = true } = options;

  return useQuery<AccountsDashboardResponseDto>({
    queryKey: ["accountsDashboard"],
    queryFn: () =>
      fetchStaffJson("/accounts/dashboard", "Accounts Head dashboard"),
    enabled,
  });
}

export function useAccountsOfficerWorkbench(options: StaffQueryOptions = {}) {
  const { enabled = true } = options;

  return useQuery<AccountsOfficerWorkbenchResponseDto>({
    queryKey: ["accountsOfficerWorkbench"],
    queryFn: () =>
      fetchStaffJson(
        "/accounts/workbench/officer",
        "Accounts Officer workbench",
      ),
    enabled,
  });
}

/**
 * 7. Audit Head dashboard + Auditor workbench
 */

export function useAuditDashboard(options: StaffQueryOptions = {}) {
  const { enabled = true } = options;

  return useQuery<AuditDashboardResponseDto>({
    queryKey: ["auditDashboard"],
    queryFn: () => fetchStaffJson("/audit/dashboard", "Audit Head dashboard"),
    enabled,
  });
}

export function useAuditorWorkbench(options: StaffQueryOptions = {}) {
  const { enabled = true } = options;

  return useQuery<AuditorWorkbenchResponseDto>({
    queryKey: ["auditorWorkbench"],
    queryFn: () =>
      fetchStaffJson("/audit/workbench/auditor", "Auditor workbench"),
    enabled,
  });
}

/**
 * 8. HR Head dashboard + HR Manager workbench
 */

export function useHrDashboard(options: StaffQueryOptions = {}) {
  const { enabled = true } = options;

  return useQuery<HrDashboardResponseDto>({
    queryKey: ["hrDashboard"],
    queryFn: () => fetchStaffJson("/hr/dashboard", "HR Head dashboard"),
    enabled,
  });
}

export function useHrManagerWorkbench(options: StaffQueryOptions = {}) {
  const { enabled = true } = options;

  return useQuery<HrManagerWorkbenchResponseDto>({
    queryKey: ["hrManagerWorkbench"],
    queryFn: () =>
      fetchStaffJson("/hr/workbench/manager", "HR Manager workbench"),
    enabled,
  });
}

/**
 * 9. IT Head dashboard + Developer & Data Scientist workbenches
 */

export function useItDashboard(options: StaffQueryOptions = {}) {
  const { enabled = true } = options;

  return useQuery<ItDashboardResponseDto>({
    queryKey: ["itDashboard"],
    queryFn: () => fetchStaffJson("/it/dashboard", "IT Head dashboard"),
    enabled,
  });
}

export function useDeveloperWorkbench(options: StaffQueryOptions = {}) {
  const { enabled = true } = options;

  return useQuery<DeveloperWorkbenchResponseDto>({
    queryKey: ["developerWorkbench"],
    queryFn: () =>
      fetchStaffJson("/it/workbench/developer", "Developer workbench"),
    enabled,
  });
}

export function useDataScientistWorkbench(options: StaffQueryOptions = {}) {
  const { enabled = true } = options;

  return useQuery<DataScientistWorkbenchResponseDto>({
    queryKey: ["dataScientistWorkbench"],
    queryFn: () =>
      fetchStaffJson(
        "/it/workbench/data-scientist",
        "Data Scientist workbench",
      ),
    enabled,
  });
}

/**
 * 10. Grievance & Feedback Head dashboard + Grievance Officer workbench
 */

export function useGrievanceDashboard(options: StaffQueryOptions = {}) {
  const { enabled = true } = options;

  return useQuery<GrievanceDashboardResponseDto>({
    queryKey: ["grievanceDashboard"],
    queryFn: () =>
      fetchStaffJson("/grievance/dashboard", "Grievance Head dashboard"),
    enabled,
  });
}

export function useGrievanceOfficerWorkbench(options: StaffQueryOptions = {}) {
  const { enabled = true } = options;

  return useQuery<GrievanceOfficerWorkbenchResponseDto>({
    queryKey: ["grievanceOfficerWorkbench"],
    queryFn: () =>
      fetchStaffJson(
        "/grievance/workbench/officer",
        "Grievance Officer workbench",
      ),
    enabled,
  });
}

/**
 * 11. Re-exports for modules still importing helpers/types directly from staff/hooks.
 */

export {
  fetchStaffDashboard,
  fetchStaffVerificationQueue,
  fetchStaffDisputeQueue,
  buildStaffVerificationQuery,
  buildStaffDisputeQuery,
  mapStaffProfileDto,
  mapStaffDashboardSummaryDto,
  mapStaffVerificationItemDto,
  mapStaffDisputeItemDto,
};

export type {
  HnbStaffRole,
  StaffDashboardScope,
  StaffVerificationQueueStatus,
  StaffDisputeStatus,
  HnbStaffProfileDto,
  StaffDashboardSummaryDto,
  StaffProviderVerificationItemDto,
  StaffDisputeQueueItemDto,
  StaffDashboardResponseDto,
  StaffVerificationQueueResponseDto,
  StaffDisputeQueueResponseDto,
  HnbStaffProfile,
  StaffDashboardSummary,
  StaffProviderVerificationItem,
  StaffDisputeQueueItem,
  StaffVerificationFilters,
  StaffDisputeFilters,
};